// @givmo/board — Board's calendar on the wire, read tolerantly (CBG-CAL1 slice 1d; the D-CBG-15
// derivation's rail #4).
//
// ══ WHY THIS COMES FIRST ════════════════════════════════════════════════════════════════════════
//
// Slice 1's backend (be#2440, be#2451, be#2452) is merged and dark. When its flags turn on, four
// things Board already reads, or is about to, start carrying calendar facts:
//
//   * a meeting's event log (`GET …/meetings/{id}/meeting-events`) gains NINE event types —
//     `invitations_sent`, `invitations_updated`, `invitations_cancelled`, `invitation_not_sent`,
//     `invitee_added`, `invitee_removed`, `calendar_moved`, `calendar_detached`, `calendar_observed` —
//     and two fields on every event, `origin` (`board` | `calendar`) and `origin_provider`; a
//     `rescheduled`, `updated` or `cancelled` can now arrive FROM the calendar, naming no actor;
//   * the meeting read gains its `calendar` block (`BoardMeetingCalendarOut`: null while both calendar
//     flags are off, or for a meeting nothing was sent for);
//   * the invitations read answers each invited seat's latest reply from their own calendar;
//   * the director's meeting read gains the director's line (`BoardDirectorMeetingCalendarOut`).
//
// The backend's rule is "new enum values only after both client decoders are tolerant"
// (`migration_safety.md`): its flags stay off until the web reads every one of these without failing.
// So each decode here takes ANY string where the generated type names a closed set, keeps a value
// this build does not know exactly as it came (a page shows it, or leaves it out, but never throws on
// it), and answers `null` / `[]` for a shape it cannot read. Nothing here composes words: the lines a
// page shows are the backend's own notes, verbatim, or the approved frames' words in the page.
//
// The shapes are the generated models with every optional field filled in with what its absence
// means and every enum widened to `string` — `director/director-wire.ts`'s idiom — so a field the
// backend adds reaches them with the next regeneration.

import type {
  BoardDirectorMeetingCalendarOut,
  BoardMeetingCalendarOut,
  BoardMeetingEventOut,
  BoardMeetingInviteeOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';

/** A string enum, as a tolerant decode keeps it: any string. */
type Widened<Value> = Value extends string ? string : Value;

/** A generated model as a tolerant decode hands it on: no field missing, every enum a string. */
type Decoded<Model> = { [Field in keyof Model]-?: Widened<Exclude<Model[Field], undefined>> };

/** A meeting's calendar block: how it reached calendars, and what holds now. */
export type MeetingCalendar = Decoded<BoardMeetingCalendarOut>;

/** One line of a meeting's append-only event log. */
export type MeetingEvent = Decoded<BoardMeetingEventOut>;

/** One seat Board invited, and its latest reply in its own calendar. */
export type MeetingInvitee = Decoded<BoardMeetingInviteeOut>;

/**
 * The invitations read: the block, and each invited seat. Outlook's `proposals` are slice 3's and
 * are not read here.
 */
export interface MeetingInvitations {
  calendar: MeetingCalendar | null;
  invitees: MeetingInvitee[];
}

/** The director's meeting line: this seat was sent the meeting, from an address or from Givmo. */
export type DirectorMeetingCalendar = Decoded<BoardDirectorMeetingCalendarOut>;

/**
 * The event types the calendar adds to a meeting's log (backend `ck_board_meeting_event_type`):
 * slice 1's nine, then CBG-FIX46's `directors_told`, an officer's "I've told everyone", whose line
 * the backend composes when the log is read, naming the officer (contract §4.2, §13).
 */
export const CALENDAR_EVENT_TYPES: readonly string[] = [
  'invitations_sent',
  'invitations_updated',
  'invitations_cancelled',
  'invitation_not_sent',
  'invitee_added',
  'invitee_removed',
  'calendar_moved',
  'calendar_detached',
  'calendar_observed',
  'directors_told',
];

/**
 * The link states in which Board keeps a meeting in a calendar: a live link, an invitation out or on
 * its way. `detached` (no longer kept in step) and `moved` (a retired link) are not.
 */
export const KEPT_IN_CALENDAR_STATES: readonly string[] = [
  'pending',
  'sent',
  'needs_attention',
  'cancelled',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A non-blank string, exactly as it came (a note is shown verbatim), or null. */
function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * The meeting read's `calendar` block, or null: absent, null (both calendar flags off, or nothing was
 * sent), or a shape with no channel or state, which no line could be read from.
 */
export function decodeMeetingCalendar(value: unknown): MeetingCalendar | null {
  if (!isRecord(value)) return null;
  const channel = text(value.channel);
  const state = text(value.state);
  if (channel === null || state === null) return null;
  const count = value.invited_count;
  return {
    channel,
    state,
    needs_attention_code: text(value.needs_attention_code),
    organizer_email: text(value.organizer_email),
    provider: text(value.provider),
    conference_join_url: text(value.conference_join_url),
    invited_count: typeof count === 'number' && Number.isInteger(count) && count >= 0 ? count : 0,
    sent_at: text(value.sent_at),
    last_checked_at: text(value.last_checked_at),
    check_failed: value.check_failed === true,
    out_of_step: value.out_of_step === true,
  };
}

/**
 * A meeting's event log, in the backend's order. Either wire shape (a bare array, or an envelope);
 * a row with no id or no type is dropped, every type is kept as it came, and an event with no
 * `origin` is Board's own (the column's server default).
 */
export function decodeMeetingEvents(body: unknown): MeetingEvent[] {
  const events: MeetingEvent[] = [];
  for (const row of extractList<unknown>(body, 'data')) {
    if (!isRecord(row)) continue;
    const id = text(row.id);
    const eventType = text(row.event_type);
    if (id === null || eventType === null) continue;
    events.push({
      id,
      meeting_id: text(row.meeting_id) ?? '',
      event_type: eventType,
      note: text(row.note),
      occurred_at: text(row.occurred_at),
      origin: text(row.origin) ?? 'board',
      origin_provider: text(row.origin_provider),
      // CBG-FIX32 (question 9): when the calendar recorded an adopted change; absent before then.
      origin_recorded_at: text(row.origin_recorded_at),
      actor_user_id: text(row.actor_user_id),
      actor_board_member_id: text(row.actor_board_member_id),
    });
  }
  return events;
}

/** Whether an event is one of the calendar's types, or a change that arrived FROM a calendar. */
export function isCalendarEvent(event: Pick<MeetingEvent, 'event_type' | 'origin'>): boolean {
  return event.origin === 'calendar' || CALENDAR_EVENT_TYPES.includes(event.event_type);
}

/** One invited seat; a row with no seat or no address is dropped. */
function decodeInvitee(row: unknown): MeetingInvitee | null {
  if (!isRecord(row)) return null;
  const seat = text(row.board_member_id);
  const address = text(row.email_sent_to);
  if (seat === null || address === null) return null;
  return {
    board_member_id: seat,
    email_sent_to: address,
    added_at: text(row.added_at),
    removed_at: text(row.removed_at),
    removed_reason: text(row.removed_reason),
    last_delivery: text(row.last_delivery),
    calendar_response: text(row.calendar_response),
    calendar_responded_at: text(row.calendar_responded_at),
  };
}

/** The invitations read: its block and its seats, or an empty answer for a shape it cannot read. */
export function decodeMeetingInvitations(body: unknown): MeetingInvitations {
  if (!isRecord(body)) return { calendar: null, invitees: [] };
  const rows = Array.isArray(body.invitees) ? body.invitees : [];
  return {
    calendar: decodeMeetingCalendar(body.calendar),
    invitees: rows
      .map(decodeInvitee)
      .filter((invitee): invitee is MeetingInvitee => invitee !== null),
  };
}

/** The director's line, or null: no invitation reached this seat, or a shape with no sender. */
export function decodeDirectorMeetingCalendar(value: unknown): DirectorMeetingCalendar | null {
  if (!isRecord(value)) return null;
  const sentFrom = text(value.sent_from);
  if (sentFrom === null) return null;
  return { sent_from: sentFrom, join_url: text(value.join_url) };
}

/**
 * Whether Board keeps this meeting in a calendar now: a live link (sent, on its way, needing
 * attention, or cancelled through it). A detached or retired link, an unknown state, and no block at
 * all are not.
 */
export function isKeptInCalendar(calendar: Pick<MeetingCalendar, 'state'> | null): boolean {
  return calendar !== null && KEPT_IN_CALENDAR_STATES.includes(calendar.state);
}
